import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import { Avatar, Button, Card, Icon, Label, Tab, TabList, TabProvider, Text } from '@gravity-ui/uikit';
import { PersonPlus } from '@gravity-ui/icons';
import type { ManagerOverviewItem } from '@odonix/contracts';
import { useManagersApi } from '../../lib/api/managers-context.js';
import { count, initials, moment, percent, rub, rubCompact } from '../../ui/format.js';
import { MetricStrip } from '../../ui/metric-strip.js';
import { Page } from '../../ui/page.js';
import { PeriodPicker } from '../../ui/period-picker.js';
import { periodOf, periodQuery } from '../../ui/period.js';
import { useGo } from '../../ui/router-link.js';
import { Empty, ErrorNote, LoadingRows } from '../../ui/states.js';
import { MANAGER_STATUS_LABEL, MANAGER_STATUS_THEME } from './labels.js';
import { ManagerFormDialog } from './manager-form.js';

/**
 * Менеджеры Odonix — карточкой на человека: процент, клиники, оплаты его
 * клиник и что ему причитается за период. Карточка ведёт в страницу
 * менеджера, где правка, премии, клиники, ссылки и журнал.
 */

const routeApi = getRouteApi('/shell/managers');

export function ManagersScreen() {
  const api = useManagersApi();
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const go = useGo();
  const period = periodOf(search);
  const show = search.show ?? 'active';
  const [creating, setCreating] = useState(false);

  const query = useQuery({
    queryKey: ['managers-overview', period.from, period.to],
    queryFn: () => api.overview(period),
    placeholderData: (previous) => previous,
  });

  const all = query.data?.managers ?? [];
  const active = all.filter((item) => item.manager.status === 'active');
  const disabled = all.filter((item) => item.manager.status === 'disabled');
  const visible = show === 'active' ? active : disabled;
  const payout = all.reduce((total, item) => total + item.payoutMinor, 0);
  const assigned = all.reduce((total, item) => total + item.tenants, 0);

  return (
    <Page
      title="Менеджеры"
      description="Ведут свои клиники: раздают реферальную ссылку, звонят, получают процент от подписок своих клиник и премии."
      actions={
        <>
          <PeriodPicker
            value={period}
            onChange={(next) => {
              void navigate({ search: (prev) => ({ ...prev, from: next.from, to: next.to }) });
            }}
          />
          <Button
            view="action"
            onClick={() => {
              setCreating(true);
            }}
          >
            <Icon data={PersonPlus} size={16} />
            Добавить менеджера
          </Button>
        </>
      }
    >
      {query.isError ? (
        <ErrorNote title="Менеджеры не загрузились" error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <MetricStrip
          loading={query.data === undefined}
          metrics={[
            { label: 'Менеджеров', value: count(active.length), hint: disabled.length === 0 ? undefined : `и ${String(disabled.length)} отключено` },
            { label: 'Клиник за менеджерами', value: count(assigned) },
            {
              label: 'Ни за кем',
              value: count(query.data?.unassignedTenants ?? 0),
              hint: 'закрепить можно в карточке клиники',
              href: '/tenants?managerId=none',
              onOpen: () => {
                go('/tenants?managerId=none');
              },
            },
            { label: 'К выплате за период', value: rub(payout), hint: 'процент и премии' },
          ]}
        />
      )}

      <TabProvider
        value={show}
        onUpdate={(value) => {
          void navigate({ search: (prev) => ({ ...prev, show: value === 'disabled' ? 'disabled' : undefined }) });
        }}
      >
        <TabList>
          <Tab value="active" counter={active.length}>
            Активные
          </Tab>
          <Tab value="disabled" counter={disabled.length}>
            Отключённые
          </Tab>
        </TabList>
      </TabProvider>

      {query.data === undefined && !query.isError ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
          <LoadingRows rows={1} height={210} />
          <LoadingRows rows={1} height={210} />
        </div>
      ) : visible.length === 0 ? (
        show === 'active' ? (
          <Empty
            title="Менеджеров пока нет"
            description="Заведите менеджера: почта, имя и процент. Ему придёт письмо со ссылкой на кабинет, где будет его реферальная ссылка."
            action={
              <Button
                view="action"
                onClick={() => {
                  setCreating(true);
                }}
              >
                Добавить менеджера
              </Button>
            }
          />
        ) : (
          <Empty title="Отключённых менеджеров нет" />
        )
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
          {visible.map((item) => (
            <ManagerCard
              key={item.manager.id}
              item={item}
              onOpen={() => {
                go(`/managers/${item.manager.id}?${periodQuery(period)}`);
              }}
            />
          ))}
        </div>
      )}

      {creating ? (
        <ManagerFormDialog
          open
          manager={null}
          onClose={() => {
            setCreating(false);
          }}
        />
      ) : null}
    </Page>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <Text variant="caption-2" color="secondary" ellipsis>
        {label}
      </Text>
      <Text variant="subheader-2" className="tabular" whiteSpace="nowrap">
        {value}
      </Text>
    </div>
  );
}

function ManagerCard({ item, onOpen }: { item: ManagerOverviewItem; onOpen: () => void }) {
  const { manager } = item;
  return (
    <Card view="outlined" type="action" onClick={onOpen} className="flex flex-col gap-4 p-5">
      <div className="flex items-start gap-3">
        <Avatar text={initials(manager.fullName)} size="l" theme="brand" />
        <div className="flex min-w-0 flex-1 flex-col">
          <Text variant="subheader-3" ellipsis>
            {manager.fullName}
          </Text>
          <Text color="secondary" ellipsis>
            {manager.email}
          </Text>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <Label theme={MANAGER_STATUS_THEME[manager.status]}>{MANAGER_STATUS_LABEL[manager.status]}</Label>
          <Text variant="caption-2" color="secondary" className="tabular">
            {percent(manager.commissionPercent)} от подписки
          </Text>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 border-t border-[var(--g-color-line-generic)] pt-4">
        <Stat label="Клиник" value={`${count(item.tenants)} · ${count(item.paying)} платят`} />
        <Stat label="Новых" value={count(item.newTenants)} />
        <Stat label="Звонков" value={count(item.calls)} />
        <Stat label="Оплаты клиник" value={rubCompact(item.revenueMinor)} />
        <Stat label="Процент" value={rubCompact(item.commissionMinor)} />
        <Stat label="К выплате" value={rubCompact(item.payoutMinor)} />
      </div>

      <Text variant="caption-2" color="secondary">
        {manager.lastLoginAt === null ? 'Ещё не входил в кабинет' : `Был в кабинете ${moment(manager.lastLoginAt)}`}
        {item.visits === 0 ? '' : ` · переходов по ссылкам: ${count(item.visits)}`}
      </Text>
    </Card>
  );
}
